import { useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, articles as allArticles } from '@/mocks/articles';

const iconColorMap: Record<string, string> = {
  accent: 'bg-accent-100/70 text-accent-700',
  primary: 'bg-primary-100/70 text-primary-700',
  secondary: 'bg-secondary-100/70 text-secondary-700',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  useEffect(() => {
    if (slug && !categories.find((c) => c.slug === slug)) {
      navigate('/category/ai-tools', { replace: true });
      return;
    }
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  const currentArticles = useMemo(() => {
    if (!slug) return [];
    return getArticlesByCategory(slug);
  }, [slug]);

  if (!category) {
    return null;
  }

  const allArticlesInCategory = currentArticles.length;
  const otherArticles = allArticles.filter((a) => a.category !== slug).slice(0, 6);

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 亚洲免费在线`,
    description: category.description,
    url: `https://akeliusi.cn/category/${category.slug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <section className="bg-background-100/50">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${iconColorMap[category.color] || iconColorMap.primary}`}>
                  <i className={`${category.icon} text-xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 flex-wrap pb-4 border-b border-background-200/70" aria-label="分类切换导航">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </nav>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <p className="text-sm text-foreground-500 mb-6">
              共找到 <strong className="text-foreground-900">{allArticlesInCategory}</strong> 篇相关文章
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </section>

          {otherArticles.length > 0 && (
            <section className="bg-background-100/50 py-12">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h3 className="text-lg font-semibold text-foreground-950 mb-6 font-heading">其他分类推荐</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {otherArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}